import {
  GeneratedMealPlan,
  IngredientQuantity,
  MealPlannerPreferences,
  MealType,
  PlannedMeal,
  Recipe,
  ScrapedProduct,
  ShoppingListItem,
  UserInventoryItem,
} from './types';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const mealTypes: MealType[] = ['breakfast', 'lunch', 'dinner'];
const unitScale = { g: 1, kg: 1000, ml: 1, l: 1000, count: 1 };

type CostResult = {
  shoppingList: ShoppingListItem[];
  totalCost: number;
  warnings: string[];
};

function toBaseQuantity(quantity: number, unit: IngredientQuantity['unit']) {
  return quantity * unitScale[unit];
}

function unitsCompatible(left: IngredientQuantity['unit'], right: IngredientQuantity['unit']) {
  if (left === right) {
    return true;
  }

  return (left === 'g' && right === 'kg') || (left === 'kg' && right === 'g')
    || (left === 'ml' && right === 'l') || (left === 'l' && right === 'ml');
}

function addIngredientQuantity(
  map: Map<string, IngredientQuantity>,
  ingredient: IngredientQuantity,
  scale = 1
) {
  const existing = map.get(ingredient.ingredientId);
  const quantity = toBaseQuantity(ingredient.quantity * scale, ingredient.unit);

  if (!existing) {
    map.set(ingredient.ingredientId, { ...ingredient, quantity, unit: ingredient.unit === 'kg' ? 'g' : ingredient.unit === 'l' ? 'ml' : ingredient.unit });
    return;
  }

  existing.quantity += quantity;
}

function aggregateNeededIngredients(meals: PlannedMeal[]) {
  const required = new Map<string, IngredientQuantity>();

  meals.forEach((meal) => {
    if (meal.leftoverSourceId) {
      return;
    }

    const scale = meal.servings / meal.recipe.baseServings;
    meal.recipe.ingredients.forEach((ingredient) => addIngredientQuantity(required, ingredient, scale));
  });

  return required;
}

function aggregateInventory(items: UserInventoryItem[]) {
  const inventory = new Map<string, number>();

  items.forEach((item) => {
    const existing = inventory.get(item.ingredientId) ?? 0;
    inventory.set(item.ingredientId, existing + toBaseQuantity(item.quantity, item.unit));
  });

  return inventory;
}

export function priceMissingIngredients(
  meals: PlannedMeal[],
  inventoryItems: UserInventoryItem[],
  products: ScrapedProduct[],
  storeId: MealPlannerPreferences['storeId']
): CostResult {
  const required = aggregateNeededIngredients(meals);
  const inventory = aggregateInventory(inventoryItems);
  const warnings: string[] = [];
  const shoppingList: ShoppingListItem[] = [];

  required.forEach((ingredient) => {
    const owned = inventory.get(ingredient.ingredientId) ?? 0;
    const quantityNeeded = Math.max(0, ingredient.quantity - owned);

    if (quantityNeeded <= 0) {
      return;
    }

    const product = products
      .filter((candidate) => (
        candidate.storeId === storeId
        && candidate.ingredientId === ingredient.ingredientId
        && unitsCompatible(candidate.unit, ingredient.unit)
      ))
      .sort((a, b) => (a.price / toBaseQuantity(a.unitQuantity, a.unit)) - (b.price / toBaseQuantity(b.unitQuantity, b.unit)))[0];

    if (!product) {
      warnings.push(`No ${storeId} price found for ${ingredient.name}; excluded from budget total.`);
      shoppingList.push({
        ingredientId: ingredient.ingredientId,
        name: ingredient.name,
        category: ingredient.category,
        quantityNeeded,
        unit: ingredient.unit,
        packages: 0,
        packageSize: 0,
        productName: 'Price unavailable',
        price: 0,
        totalPrice: 0,
        missingPrice: true,
      });
      return;
    }

    const packageSize = toBaseQuantity(product.unitQuantity, product.unit);
    const packages = Math.ceil(quantityNeeded / packageSize);
    shoppingList.push({
      ingredientId: ingredient.ingredientId,
      name: ingredient.name,
      category: product.category,
      quantityNeeded,
      unit: product.unit === 'kg' ? 'g' : product.unit === 'l' ? 'ml' : product.unit,
      packages,
      packageSize,
      productName: product.productName,
      price: product.price,
      totalPrice: Number((packages * product.price).toFixed(2)),
    });
  });

  return {
    shoppingList: shoppingList.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name)),
    totalCost: Number(shoppingList.reduce((sum, item) => sum + item.totalPrice, 0).toFixed(2)),
    warnings,
  };
}

function recipeMatchesPreferences(recipe: Recipe, preferences: MealPlannerPreferences) {
  return preferences.dietaryRestrictions.every((restriction) => recipe.dietaryTags.includes(restriction));
}

function getRecipeEstimatedCost(recipe: Recipe, products: ScrapedProduct[], preferences: MealPlannerPreferences) {
  const meal: PlannedMeal = {
    id: `estimate-${recipe.id}`,
    day: 'Estimate',
    mealType: recipe.mealTypes[0],
    recipe,
    servings: preferences.people,
  };

  return priceMissingIngredients([meal], [], products, preferences.storeId).totalCost / Math.max(1, preferences.people);
}

function getPackingScore(recipe: Recipe, meals: PlannedMeal[], products: ScrapedProduct[], preferences: MealPlannerPreferences) {
  const current = priceMissingIngredients(meals, [], products, preferences.storeId).shoppingList;
  const leftovers = new Map(current.map((item) => [
    item.ingredientId,
    Math.max(0, item.packages * item.packageSize - item.quantityNeeded),
  ]));

  return recipe.ingredients.reduce((score, ingredient) => {
    const leftover = leftovers.get(ingredient.ingredientId) ?? 0;
    const needed = toBaseQuantity(ingredient.quantity, ingredient.unit);
    return score + Math.min(leftover, needed) / Math.max(needed, 1);
  }, 0);
}

function pickBestRecipe(
  recipes: Recipe[],
  meals: PlannedMeal[],
  products: ScrapedProduct[],
  preferences: MealPlannerPreferences,
  mealType: MealType
) {
  const eligible = recipes.filter((recipe) => recipe.mealTypes.includes(mealType));

  return eligible
    .map((recipe) => ({
      recipe,
      score:
        getRecipeEstimatedCost(recipe, products, preferences)
        - getPackingScore(recipe, meals, products, preferences) * 0.8
        + (recipe.styleTags.includes(preferences.cookingStyle) ? -0.35 : 0)
        + (meals.some((meal) => meal.recipe.id === recipe.id) ? 0.45 : 0),
    }))
    .sort((a, b) => a.score - b.score)[0]?.recipe;
}

function buildInitialMeals(
  recipes: Recipe[],
  products: ScrapedProduct[],
  preferences: MealPlannerPreferences
) {
  const meals: PlannedMeal[] = [];
  const breakfast = pickBestRecipe(recipes, meals, products, preferences, 'breakfast');

  days.forEach((day) => {
    if (breakfast) {
      meals.push({ id: `${day}-breakfast-${breakfast.id}`, day, mealType: 'breakfast', recipe: breakfast, servings: preferences.people });
    }
  });

  if (preferences.cookingStyle === 'batch-prep') {
    const batchRecipes = recipes
      .filter((recipe) => recipe.mealTypes.includes('dinner') && recipe.styleTags.includes('batch-prep'))
      .sort((a, b) => getRecipeEstimatedCost(a, products, preferences) - getRecipeEstimatedCost(b, products, preferences))
      .slice(0, 2);

    days.forEach((day, index) => {
      const recipe = batchRecipes[index < 4 ? 0 : 1] ?? batchRecipes[0];
      if (!recipe) return;
      const batchId = index < 4 ? `${recipe.id}-monday-batch` : `${recipe.id}-thursday-batch`;

      if (index === 0 || index === 4) {
        const servings = preferences.people * (index === 0 ? 8 : 6);
        meals.push({ id: batchId, day, mealType: 'dinner', recipe, servings });
        return;
      }

      meals.push({ id: `${day}-lunch-${recipe.id}`, day, mealType: 'lunch', recipe, servings: preferences.people, leftoverSourceId: batchId });
      meals.push({ id: `${day}-dinner-${recipe.id}`, day, mealType: 'dinner', recipe, servings: preferences.people, leftoverSourceId: batchId });
    });
    return meals;
  }

  days.forEach((day, index) => {
    const dinner = pickBestRecipe(recipes, meals, products, preferences, 'dinner');
    if (dinner) {
      const servings = preferences.cookingStyle === 'leftover-rotation' ? preferences.people * 2 : preferences.people;
      meals.push({ id: `${day}-dinner-${dinner.id}`, day, mealType: 'dinner', recipe: dinner, servings });

      if (preferences.cookingStyle === 'leftover-rotation') {
        meals.push({
          id: `${day}-lunch-leftover-${dinner.id}`,
          day,
          mealType: 'lunch',
          recipe: dinner,
          servings: preferences.people,
          leftoverSourceId: `${days[Math.max(0, index - 1)]}-dinner-${dinner.id}`,
        });
      }
    }

    if (preferences.cookingStyle === 'fresh-varied') {
      const lunch = pickBestRecipe(recipes, meals, products, preferences, 'lunch');
      if (lunch) {
        meals.push({ id: `${day}-lunch-${lunch.id}`, day, mealType: 'lunch', recipe: lunch, servings: preferences.people });
      }
    }
  });

  return meals;
}

function replaceHighestCostMeal(
  meals: PlannedMeal[],
  recipes: Recipe[],
  products: ScrapedProduct[],
  inventory: UserInventoryItem[],
  preferences: MealPlannerPreferences
) {
  const replaceableMeals = meals.filter((meal) => meal.mealType !== 'breakfast' && !meal.leftoverSourceId);
  const mealCosts = replaceableMeals
    .map((meal) => ({
      meal,
      cost: priceMissingIngredients([meal], inventory, products, preferences.storeId).totalCost,
    }))
    .sort((a, b) => b.cost - a.cost);

  const target = mealCosts[0]?.meal;
  if (!target) {
    return meals;
  }

  const alternatives = recipes
    .filter((recipe) => recipe.id !== target.recipe.id && recipe.mealTypes.includes(target.mealType))
    .sort((a, b) => getRecipeEstimatedCost(a, products, preferences) - getRecipeEstimatedCost(b, products, preferences));

  const replacement = alternatives[0];
  if (!replacement) {
    return meals;
  }

  return meals.map((meal) => (
    meal.id === target.id ? { ...meal, recipe: replacement, id: `${meal.day}-${meal.mealType}-${replacement.id}` } : meal
  ));
}

export function generateBudgetMealPlan(
  allRecipes: Recipe[],
  products: ScrapedProduct[],
  inventory: UserInventoryItem[],
  preferences: MealPlannerPreferences
): GeneratedMealPlan {
  const safePreferences = {
    ...preferences,
    weeklyBudgetCap: Math.max(0, Number(preferences.weeklyBudgetCap) || 0),
    people: Math.max(1, Math.round(Number(preferences.people) || 1)),
  };
  const recipes = allRecipes.filter((recipe) => recipeMatchesPreferences(recipe, safePreferences));
  const warnings: string[] = [];

  if (recipes.length === 0) {
    return {
      meals: [],
      shoppingList: [],
      totalCost: 0,
      budgetCap: safePreferences.weeklyBudgetCap,
      budgetStatus: 'under',
      warnings: ['No recipes match the selected dietary restrictions.'],
    };
  }

  let meals = buildInitialMeals(recipes, products, safePreferences);
  let priced = priceMissingIngredients(meals, inventory, products, safePreferences.storeId);
  let attempts = 0;

  while (priced.totalCost > safePreferences.weeklyBudgetCap && attempts < 40) {
    const nextMeals = replaceHighestCostMeal(meals, recipes, products, inventory, safePreferences);
    if (nextMeals === meals || JSON.stringify(nextMeals) === JSON.stringify(meals)) {
      break;
    }

    meals = nextMeals;
    priced = priceMissingIngredients(meals, inventory, products, safePreferences.storeId);
    attempts += 1;
  }

  if (priced.totalCost > safePreferences.weeklyBudgetCap) {
    warnings.push('The cheapest compatible plan still exceeds the budget cap. Try batch prep, fewer restrictions, or a higher cap.');
  }

  return {
    meals: meals.sort((a, b) => days.indexOf(a.day) - days.indexOf(b.day) || mealTypes.indexOf(a.mealType) - mealTypes.indexOf(b.mealType)),
    shoppingList: priced.shoppingList,
    totalCost: priced.totalCost,
    budgetCap: safePreferences.weeklyBudgetCap,
    budgetStatus: priced.totalCost > safePreferences.weeklyBudgetCap ? 'over' : priced.totalCost === safePreferences.weeklyBudgetCap ? 'at' : 'under',
    warnings: [...warnings, ...priced.warnings],
  };
}
